import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { filter, get, isArray, isBoolean, isNil, join, keys, map, sortBy } from 'lodash'
import apiGet from 'lib/network/apiGet'
import apiPut from 'lib/network/apiPut'
import notify from 'components/atoms/Notify'
import Switch from 'components/atoms/Switch'
import Table from 'components/pageComponents/Table'
import EmptyState from 'components/pageComponents/EmptyState'
import LoadingIcon from 'components/atoms/LoadingIcon'
import Modal from 'components/molecules/Modal'
import RefundRuleModal from './modals/RefundRuleModal'
import { Badge, btn, fmtDate } from './refundShared'

/** Set conditions as compact "key: value" chips; unset = "don't care". */
export const ConditionTags = ({ conditions }) => {
  const { t } = useTranslation()
  const set = filter(keys(conditions), key => !isNil(conditions[key]) && !(isArray(conditions[key]) && conditions[key].length === 0))
  if (!set.length) return <span className="text-xs text-gray-400">{t('refundEngine.rules.anyBooking')}</span>
  const show = value => {
    if (isBoolean(value)) return value ? t('refundEngine.common.yes') : t('refundEngine.common.no')
    return isArray(value) ? join(value, ', ') : value
  }
  return (
    <div className="flex flex-wrap gap-1 max-w-md whitespace-normal">
      {map(set, key => (
        <Badge key={key}>
          {t(`refundEngine.conditions.${key}`)}: {show(conditions[key])}
        </Badge>
      ))}
    </div>
  )
}

const RefundRules = ({ settings }) => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language === 'ar' ? 'ar' : 'en'
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState(null) // null = closed, {} = new rule, rule = edit
  const [history, setHistory] = useState(null)

  const fetchRules = async () => {
    setLoading(true)
    const response = await apiGet('refund-rules')
    setLoading(false)
    if (response?.success) setRules(sortBy(response.data || [], 'priority'))
  }

  useEffect(() => {
    fetchRules()
  }, [])

  const toggleActive = async (rule, isActive) => {
    const response = await apiPut(`refund-rules/${rule.code}`, { isActive })
    if (response?.success) {
      notify.success(t('refundEngine.rules.saved'))
      fetchRules()
    }
  }

  const openHistory = async rule => {
    const response = await apiGet(`refund-rules/${rule.code}/history`)
    if (response?.success) setHistory({ code: rule.code, versions: response.data || [] })
  }

  const yesNo = value => (value ? <Badge tone="blue">{t('refundEngine.common.yes')}</Badge> : <span className="text-gray-400">{t('refundEngine.common.no')}</span>)
  const deductionTags = rule => (
    <div className="flex flex-wrap gap-1">
      {map(
        filter(keys(rule.deductions), key => get(rule, `deductions.${key}`)),
        key => (
          <Badge key={key} tone="red">
            {t(`refundEngine.deductions.${key}`)}
          </Badge>
        )
      )}
    </div>
  )

  const columns = [
    { name: t('refundEngine.rules.columns.priority'), rendered: rule => <span className="tabular-nums text-gray-700">{rule.priority}</span> },
    { name: t('refundEngine.rules.columns.code'), rendered: rule => <span className="font-semibold text-gray-900">{rule.code}</span> },
    { name: t('refundEngine.rules.columns.name'), rendered: rule => get(rule, `name.${lang}`) || get(rule, 'name.en') },
    { name: t('refundEngine.rules.columns.refundPercent'), rendered: rule => <div className="text-right rtl:text-left font-medium tabular-nums">{rule.refundPercent}%</div> },
    { name: t('refundEngine.rules.columns.refundBase'), rendered: rule => (rule.refundBase ? t(`refundEngine.refundBase.${rule.refundBase}`) : '-') },
    { name: t('refundEngine.rules.columns.deductions'), rendered: deductionTags },
    { name: t('refundEngine.rules.fields.conditions'), rendered: rule => <ConditionTags conditions={rule.conditions || {}} /> },
    { name: t('refundEngine.rules.columns.addOn'), rendered: rule => yesNo(rule.isAddOn) },
    { name: t('refundEngine.rules.columns.approval'), rendered: rule => yesNo(rule.requiresApproval) },
    { name: t('refundEngine.rules.columns.active'), rendered: rule => <Switch value={Boolean(rule.isActive)} onChange={checked => toggleActive(rule, checked)} /> },
    { name: t('refundEngine.common.version'), rendered: rule => <span className="text-gray-500">v{rule.version}</span> },
    {
      name: t('refundEngine.common.actions'),
      rendered: rule => (
        <div className="flex items-center gap-2">
          <button type="button" className={btn('secondary', true)} onClick={() => setEditing(rule)}>
            {t('refundEngine.common.edit')}
          </button>
          <button type="button" className={btn('secondary', true)} onClick={() => openHistory(rule)}>
            {t('refundEngine.common.history')}
          </button>
        </div>
      ),
    },
  ]

  const historyColumns = [
    { name: t('refundEngine.common.version'), rendered: v => `v${v.version}` },
    { name: t('refundEngine.common.date'), rendered: v => fmtDate(v.createdAt) },
    { name: t('refundEngine.common.by'), rendered: v => get(v, 'createdBy.fullName', '-') },
    { name: t('refundEngine.rules.columns.priority'), rendered: v => v.priority },
    { name: t('refundEngine.rules.columns.refundPercent'), rendered: v => <div className="text-right rtl:text-left tabular-nums">{v.refundPercent}%</div> },
    { name: t('refundEngine.rules.columns.active'), rendered: v => yesNo(v.isActive) },
    { name: t('refundEngine.rules.fields.conditions'), rendered: v => <ConditionTags conditions={v.conditions || {}} /> },
  ]

  return (
    <div>
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-4 border-b border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-500">{t('refundEngine.rules.intro')}</p>
        <button type="button" className={btn('primary')} onClick={() => setEditing({})}>
          {t('refundEngine.rules.addRule')}
        </button>
      </div>

      {loading && !rules.length ? (
        <div className="flex justify-center py-16">
          <LoadingIcon />
        </div>
      ) : rules.length === 0 ? (
        <EmptyState v2 description={t('common.noData')} />
      ) : (
        <Table compact hasMore={false} fetchData={() => {}} data={rules} columns={columns} />
      )}

      {editing && (
        <RefundRuleModal
          open
          rule={editing.code ? editing : null}
          settings={settings}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            fetchRules()
          }}
        />
      )}

      <Modal
        open={Boolean(history)}
        closeable
        onClose={() => setHistory(null)}
        title={`${t('refundEngine.common.history')} — ${get(history, 'code', '')}`}
        padding="p-0"
        dimensions={{ width: '94vw', maxWidth: '960px', height: 'auto', maxHeight: '80vh' }}
      >
        {history &&
          (history.versions.length ? (
            <Table compact hasMore={false} fetchData={() => {}} data={history.versions} columns={historyColumns} />
          ) : (
            <EmptyState v2 description={t('common.noData')} />
          ))}
      </Modal>
    </div>
  )
}

export default RefundRules
